// Typed fetch wrappers. Every call goes through `apiFetch` so we can
// centralize error handling, JSON parsing, and credential carrying.

import type {
  User,
  Draft,
  DraftListItem,
  DraftStatus,
  Block,
  RecentCoverageDraft,
  AdminUserView,
  Role,
  Event,
  EventListItem,
  CreateEventRequest,
  UpdateEventRequest,
  SignupPage,
  SignupPageListItem,
  CreateSignupPageRequest,
  UpdateSignupPageRequest,
  SiteSettingsWelcomeEmail,
  UpdateSiteSettingsWelcomeEmailRequest,
  EmailProvider,
  EmailProviderListItem,
  CreateEmailProviderRequest,
  UpdateEmailProviderRequest,
  SubscriberListResponse,
  SubscriberStatus,
  SubscriberDetail,
  SubscriberEventListResponse,
  UpdateSubscriberRequest,
  CreateNewsletterSendRequest,
  CreateNewsletterSendResponse,
  NewsletterSendListResponse,
  NewsletterSendDetailResponse,
  BlacklistRuleListResponse,
  BlacklistRuleMatchesResponse,
  CreateBlacklistRuleRequest,
  UpdateBlacklistRuleRequest,
  Source,
  SourceListItem,
  SourcePermissionCheck,
  CreateSourceRequest,
  UpdateSourceRequest,
  PipelineRun,
  Digest,
  SnapshotDetail,
} from '@shared/types';

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: Record<string, unknown>) {
    super(message);
  }
}

async function apiFetch<T>(input: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(input, {
    credentials: 'same-origin',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });
  const text = await res.text();
  const body = text ? (JSON.parse(text) as Record<string, unknown>) : ({} as Record<string, unknown>);
  if (!res.ok) {
    throw new ApiError(
      res.status,
      (body.code as string) || 'unknown',
      (body.error as string) || res.statusText,
      body.details as Record<string, unknown> | undefined,
    );
  }
  return body as T;
}

// ============================================================
// AUTH
// ============================================================
export const authApi = {
  login: (email: string, password: string) =>
    apiFetch<{ user: User }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => apiFetch<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  me: () => apiFetch<{ user: User; passkeys: PasskeySummary[] }>('/api/auth/me'),

  passkeyAuthStart: () =>
    apiFetch<unknown>('/api/auth/passkey/auth/start', { method: 'POST' }),
  passkeyAuthFinish: (response: unknown) =>
    apiFetch<{ user: User }>('/api/auth/passkey/auth/finish', {
      method: 'POST',
      body: JSON.stringify({ response }),
    }),

  passkeyRegisterStart: () =>
    apiFetch<unknown>('/api/auth/passkey/register/start', { method: 'POST' }),
  passkeyRegisterFinish: (response: unknown, deviceLabel: string | null) =>
    apiFetch<{ ok: true; credential_id: string }>('/api/auth/passkey/register/finish', {
      method: 'POST',
      body: JSON.stringify({ response, device_label: deviceLabel }),
    }),

  passkeyDelete: (id: string) =>
    apiFetch<{ ok: true }>(`/api/auth/passkey/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  invitePreview: (token: string) =>
    apiFetch<{ email: string; display_name: string | null }>('/api/auth/invite/preview', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  inviteAccept: (token: string, password: string) =>
    apiFetch<{ user: User }>('/api/auth/invite/accept', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),

  passwordResetRequest: (email: string) =>
    apiFetch<{ ok: true }>('/api/auth/password/reset-request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  passwordReset: (token: string, password: string) =>
    apiFetch<{ user: User }>('/api/auth/password/reset', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),
  passwordChange: (current: string, next: string) =>
    apiFetch<{ ok: true }>('/api/auth/password/change', {
      method: 'POST',
      body: JSON.stringify({ current, next }),
    }),
};

export interface PasskeySummary {
  id: string;
  device_label: string | null;
  created_at: number;
  last_used_at: number | null;
}

// ============================================================
// DRAFTS
// ============================================================
export const draftsApi = {
  list: () => apiFetch<{ drafts: DraftListItem[] }>('/api/drafts'),
  recentCoverage: (opts?: { days?: number; excludeDraftId?: string }) => {
    const sp = new URLSearchParams();
    if (opts?.days) sp.set('days', String(opts.days));
    if (opts?.excludeDraftId) sp.set('exclude', opts.excludeDraftId);
    const qs = sp.toString() ? `?${sp.toString()}` : '';
    return apiFetch<{ drafts: RecentCoverageDraft[] }>(`/api/drafts/recent-coverage${qs}`);
  },
  get: (id: string) => apiFetch<{ draft: Draft }>(`/api/drafts/${encodeURIComponent(id)}`),
  create: (initial?: { name?: string; blocks?: Block[] }) =>
    apiFetch<{ id: string; name: string; status: DraftStatus; updated_at: number; created_at: number }>(
      '/api/drafts',
      { method: 'POST', body: JSON.stringify(initial || {}) },
    ),
  update: (
    id: string,
    body: {
      name?: string;
      subject?: string | null;
      preheader?: string | null;
      blocks?: Block[];
      status?: DraftStatus;
      expected_updated_at: number;
    },
  ) =>
    apiFetch<{ id: string; updated_at: number }>(`/api/drafts/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  delete: (id: string) =>
    apiFetch<{ ok: true }>(`/api/drafts/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  snapshot: (id: string) =>
    apiFetch<{ ok: true }>(`/api/drafts/${encodeURIComponent(id)}/snapshot`, { method: 'POST' }),
};

// ============================================================
// USERS (admin only)
// ============================================================
export const usersApi = {
  list: () => apiFetch<{ users: AdminUserView[] }>('/api/users'),
  invite: (email: string, displayName: string | undefined, role: Role) =>
    apiFetch<{ id: string; invite_url: string }>('/api/users', {
      method: 'POST',
      body: JSON.stringify({ email, display_name: displayName, role }),
    }),
  update: (id: string, body: { display_name?: string; role?: Role }) =>
    apiFetch<{ ok: true }>(`/api/users/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  suspend: (id: string) =>
    apiFetch<{ ok: true }>(`/api/users/${encodeURIComponent(id)}/suspend`, { method: 'POST' }),
  reactivate: (id: string) =>
    apiFetch<{ ok: true }>(`/api/users/${encodeURIComponent(id)}/reactivate`, { method: 'POST' }),
  passwordReset: (id: string) =>
    apiFetch<{ ok: true }>(`/api/users/${encodeURIComponent(id)}/password-reset`, { method: 'POST' }),
  remove: (id: string) =>
    apiFetch<{ ok: true }>(`/api/users/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

// ============================================================
// EVENTS
// ============================================================
function eventsListQs(params?: {
  from?: string;
  to?: string;
  include_hidden?: boolean;
  include_past?: boolean;
}): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  if (params.from) sp.set('from', params.from);
  if (params.to) sp.set('to', params.to);
  if (params.include_hidden) sp.set('include_hidden', '1');
  if (params.include_past) sp.set('include_past', '1');
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const eventsApi = {
  list: (params?: { from?: string; to?: string; include_hidden?: boolean; include_past?: boolean }) =>
    apiFetch<{ events: EventListItem[] }>(`/api/events${eventsListQs(params)}`),
  get: (id: string) => apiFetch<{ event: Event }>(`/api/events/${encodeURIComponent(id)}`),
  create: (body: CreateEventRequest) =>
    apiFetch<{ id: string; updated_at: number; created_at: number }>('/api/events', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (id: string, body: UpdateEventRequest) =>
    apiFetch<{ id: string; updated_at: number }>(`/api/events/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  delete: (id: string) =>
    apiFetch<{ ok: true }>(`/api/events/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

// ============================================================
// SIGNUP PAGES
// ============================================================
export const signupPagesApi = {
  list: () => apiFetch<{ pages: SignupPageListItem[] }>('/api/signup-pages'),
  get: (id: string) =>
    apiFetch<{ page: SignupPage }>(`/api/signup-pages/${encodeURIComponent(id)}`),
  create: (body: CreateSignupPageRequest) =>
    apiFetch<{ id: string; slug: string; updated_at: number; created_at: number }>(
      '/api/signup-pages',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  update: (id: string, body: UpdateSignupPageRequest) =>
    apiFetch<{ id: string; updated_at: number }>(`/api/signup-pages/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  delete: (id: string) =>
    apiFetch<{ ok: true }>(`/api/signup-pages/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

// ============================================================
// SITE SETTINGS (admin only)
// ============================================================
export const siteSettingsApi = {
  getWelcomeEmail: () =>
    apiFetch<{ settings: SiteSettingsWelcomeEmail }>(
      '/api/site-settings/welcome-email',
    ),
  updateWelcomeEmail: (body: UpdateSiteSettingsWelcomeEmailRequest) =>
    apiFetch<{ updated_at: number }>('/api/site-settings/welcome-email', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
};

// ============================================================
// EMAIL PROVIDERS (admin only)
// ============================================================
export const emailProvidersApi = {
  list: () => apiFetch<{ providers: EmailProviderListItem[] }>('/api/email-providers'),
  get: (id: string) =>
    apiFetch<{ provider: EmailProvider }>(`/api/email-providers/${encodeURIComponent(id)}`),
  create: (body: CreateEmailProviderRequest) =>
    apiFetch<{ id: string; updated_at: number; created_at: number }>(
      '/api/email-providers',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  update: (id: string, body: UpdateEmailProviderRequest) =>
    apiFetch<{ id: string; updated_at: number }>(
      `/api/email-providers/${encodeURIComponent(id)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),
  delete: (id: string) =>
    apiFetch<{ ok: true }>(`/api/email-providers/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
};

// ============================================================
// SUBSCRIBERS
// ============================================================
function subscribersListQs(params?: {
  source?: string;
  status?: SubscriberStatus;
  page?: number;
  page_size?: number;
}): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  if (params.source) sp.set('source', params.source);
  if (params.status) sp.set('status', params.status);
  if (params.page) sp.set('page', String(params.page));
  if (params.page_size) sp.set('page_size', String(params.page_size));
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const subscribersApi = {
  list: (params?: {
    source?: string;
    status?: SubscriberStatus;
    page?: number;
    page_size?: number;
  }) =>
    apiFetch<SubscriberListResponse>(`/api/subscribers${subscribersListQs(params)}`),
  get: (id: string) =>
    apiFetch<{ subscriber: SubscriberDetail }>(
      `/api/subscribers/${encodeURIComponent(id)}`,
    ),
  update: (id: string, body: UpdateSubscriberRequest) =>
    apiFetch<{ id: string; updated_at: number }>(
      `/api/subscribers/${encodeURIComponent(id)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),
  remove: (id: string) =>
    apiFetch<{ ok: true }>(`/api/subscribers/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  listEvents: (id: string, params?: { page?: number; page_size?: number }) => {
    const sp = new URLSearchParams();
    if (params?.page) sp.set('page', String(params.page));
    if (params?.page_size) sp.set('page_size', String(params.page_size));
    const qs = sp.toString() ? `?${sp.toString()}` : '';
    return apiFetch<SubscriberEventListResponse>(
      `/api/subscribers/${encodeURIComponent(id)}/events${qs}`,
    );
  },
};

// ============================================================
// BLACKLIST RULES (admin only)
// ============================================================
export const blacklistApi = {
  list: () => apiFetch<BlacklistRuleListResponse>('/api/blacklist'),
  get: (id: string) =>
    apiFetch<BlacklistRuleMatchesResponse>(
      `/api/blacklist/${encodeURIComponent(id)}`,
    ),
  create: (body: CreateBlacklistRuleRequest) =>
    apiFetch<{ id: string; updated_at: number; created_at: number }>(
      '/api/blacklist',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  update: (id: string, body: UpdateBlacklistRuleRequest) =>
    apiFetch<{ id: string; updated_at: number }>(
      `/api/blacklist/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  remove: (id: string) =>
    apiFetch<{ ok: true }>(`/api/blacklist/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
};

// ============================================================
// NEWSLETTER (sends + per-draft send pipeline)
// ============================================================
export const newsletterApi = {
  listSends: (params?: { page?: number; page_size?: number }) => {
    const sp = new URLSearchParams();
    if (params?.page) sp.set('page', String(params.page));
    if (params?.page_size) sp.set('page_size', String(params.page_size));
    const qs = sp.toString() ? `?${sp.toString()}` : '';
    return apiFetch<NewsletterSendListResponse>(`/api/sends${qs}`);
  },
  getSend: (id: string, params?: { page?: number; page_size?: number }) => {
    const sp = new URLSearchParams();
    if (params?.page) sp.set('page', String(params.page));
    if (params?.page_size) sp.set('page_size', String(params.page_size));
    const qs = sp.toString() ? `?${sp.toString()}` : '';
    return apiFetch<NewsletterSendDetailResponse>(
      `/api/sends/${encodeURIComponent(id)}${qs}`,
    );
  },
  send: (draftId: string, body: CreateNewsletterSendRequest) =>
    apiFetch<CreateNewsletterSendResponse | { ok: true; test: true }>(
      `/api/drafts/${encodeURIComponent(draftId)}/send`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
};

// ============================================================
// PIPELINE — source registry, runs, and the daily digest.
//
// The registry is configuration data: adding or disabling a source is
// a row edit here, never a deploy.
// ============================================================
export const sourcesApi = {
  list: (params?: { category?: string; include_disabled?: boolean }) => {
    const qs = new URLSearchParams();
    if (params?.category) qs.set('category', params.category);
    if (params?.include_disabled === false) qs.set('include_disabled', '0');
    const q = qs.toString();
    return apiFetch<{ sources: SourceListItem[] }>(`/api/sources${q ? `?${q}` : ''}`);
  },
  get: (id: string) =>
    apiFetch<{ source: Source; permissions: SourcePermissionCheck[] }>(
      `/api/sources/${encodeURIComponent(id)}`,
    ),
  create: (body: CreateSourceRequest) =>
    apiFetch<{ id: string; slug: string; created_at: number; updated_at: number }>('/api/sources', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (id: string, body: UpdateSourceRequest & { disabled_reason?: string | null }) =>
    apiFetch<{ id: string; updated_at: number }>(`/api/sources/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  delete: (id: string) =>
    apiFetch<{ ok: true }>(`/api/sources/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // Admin-only manual triggers. Both also run on cron; these exist so
  // a registry change can be validated without waiting for 03:00 UTC.
  runAudit: () => apiFetch<{ summary: unknown }>('/api/sources/audit', { method: 'POST' }),
  runRetrieval: () => apiFetch<{ summary: unknown }>('/api/sources/retrieve', { method: 'POST' }),
};

export const pipelineApi = {
  runs: (params?: { kind?: 'retrieval' | 'permission_audit'; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.kind) qs.set('kind', params.kind);
    if (params?.limit) qs.set('limit', String(params.limit));
    const q = qs.toString();
    return apiFetch<{ runs: PipelineRun[] }>(`/api/pipeline-runs${q ? `?${q}` : ''}`);
  },
  // `date` accepts "today", which the server resolves in
  // America/New_York rather than UTC.
  digest: (date: string) =>
    apiFetch<{ digest: Digest }>(`/api/digest/${encodeURIComponent(date)}`),
  // The captured bytes plus their provenance — what was scraped, from
  // where, when the source published it, and where it lives in R2.
  snapshot: (id: string) =>
    apiFetch<{ snapshot: SnapshotDetail; content: string | null }>(
      `/api/snapshots/${encodeURIComponent(id)}`,
    ),
};
